import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ModalType } from '../types';

interface BottomSectionsProps {
  onOpenModal: (type: ModalType) => void;
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
interface ToastProps {
  message: string;
  visible: boolean;
  onHide: () => void;
  light?: boolean;
}

const Toast: React.FC<ToastProps> = ({ message, visible, onHide, light }) => {
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(onHide, 4000);
    return () => clearTimeout(t);
  }, [visible, onHide]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 36,
        left: '50%',
        transform: `translateX(-50%) translateY(${visible ? 0 : 20}px)`,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: light ? '#fefcf5' : '#1a1714',
        border: `1px solid rgba(184,154,90,${light ? '0.35' : '0.22'})`,
        boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
        padding: '14px 22px',
        minWidth: 280,
        maxWidth: 420,
      }}
    >
      <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(184,154,90,0.15)', border: '1px solid rgba(184,154,90,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="#b89a5a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11, fontWeight: 400, color: light ? '#4a3e2a' : '#f2ece0', letterSpacing: '0.04em', lineHeight: 1.5, flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onHide}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: light ? '#9a8e78' : '#78736c', fontSize: 14, lineHeight: 1, padding: 2, flexShrink: 0, transition: 'color 0.2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#b89a5a')}
        onMouseLeave={(e) => (e.currentTarget.style.color = light ? '#9a8e78' : '#78736c')}
        aria-label="Dismiss"
      >✕</button>
    </div>
  );
};


// ─── INSIGHTS ────────────────────────────────────────────────────────────────
export const Insights: React.FC = () => {
  const scriptContainerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const hideToast = useCallback(() => setToastVisible(false), []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const container = scriptContainerRef.current;
    if (!container) return;

    const script = document.createElement('script');
    script.src = 'https://eocampaign1.com/form/6c0357c8-53d9-11f1-8c62-ebc20a4906e6.js';
    script.async = true;
    script.setAttribute('data-form', '6c0357c8-53d9-11f1-8c62-ebc20a4906e6');
    container.appendChild(script);

    const observer = new MutationObserver(() => {
      const inputs = container.querySelectorAll<HTMLInputElement>('input[type="text"], input[type="email"]');
      const formEl = container.querySelector('form');
      const formGone = !formEl;
      const formHidden = formEl
        ? getComputedStyle(formEl).display === 'none' || formEl.style.display === 'none'
        : false;
      if (formGone || formHidden) {
        setToastVisible(true);
        inputs.forEach((el) => { el.value = ''; });
      }
    });

    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    observerRef.current = observer;
  }, []);

  return (
    <section
      id="insights"
      className="relative py-[120px] px-10"
      style={{
        background: 'linear-gradient(158deg, #faf4e8 0%, #f5ecce 38%, #ede0b4 72%, #e8d89e 100%)',
        borderTop: '1px solid rgba(184,154,90,0.18)',
        borderBottom: '1px solid rgba(184,154,90,0.18)',
      }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ width: 600, height: 600, background: 'radial-gradient(circle, rgba(184,154,90,0.1) 0%, transparent 70%)' }}
      />

      <div className="max-w-[680px] mx-auto text-center relative z-10">
        <div className="flex items-center justify-center gap-[14px] mb-5" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#8a7040', fontFamily: 'Montserrat, sans-serif' }}>
          <span style={{ width: 28, height: 1, background: '#b89a5a', display: 'inline-block' }} />
          HNL Insights
          <span style={{ width: 28, height: 1, background: '#b89a5a', display: 'inline-block' }} />
        </div>
        <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 300, color: '#1a130a', marginBottom: 16, lineHeight: 1.15 }}>
          Sharp thinking
          <br />on <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>brand growth.</em>
        </h2>
        <p style={{ fontSize: 13, fontWeight: 300, color: '#5a4e38', lineHeight: 2, marginBottom: 44, letterSpacing: '0.02em' }}>
          Strategy, positioning, content systems, paid growth, operations, and conversion —
          distilled into precise, actionable intelligence for serious brand builders.
          No filler. No noise. Only the thinking that moves things.
        </p>

        <div ref={scriptContainerRef} className="eoc-form-wrap" />

        <p style={{ fontSize: 10, fontWeight: 300, color: '#9a8e78', letterSpacing: '0.06em', marginTop: 16 }}>
          No spam. No noise. Unsubscribe anytime.
        </p>
      </div>

      <style>{`
        .eoc-form-wrap form, .eoc-form-wrap .eoc-form {
          display: flex !important; flex-direction: column !important;
          gap: 12px !important; max-width: 480px !important; margin: 0 auto !important;
        }
        .eoc-form-wrap input[type="text"], .eoc-form-wrap input[type="email"] {
          width: 100% !important; background: rgba(255,248,225,0.55) !important;
          border: 1px solid rgba(184,154,90,0.3) !important; border-radius: 0 !important;
          color: #1a130a !important; font-family: Montserrat, sans-serif !important;
          font-size: 13px !important; font-weight: 300 !important;
          padding: 14px 20px !important; outline: none !important;
          letter-spacing: 0.02em !important; box-shadow: none !important;
        }
        .eoc-form-wrap input[type="text"]:focus, .eoc-form-wrap input[type="email"]:focus {
          border-color: rgba(184,154,90,0.7) !important;
        }
        .eoc-form-wrap input[type="submit"], .eoc-form-wrap button[type="submit"] {
          width: 100% !important; background: #b89a5a !important; color: #0d0c0b !important;
          border: none !important; border-radius: 0 !important;
          font-family: Montserrat, sans-serif !important; font-size: 10px !important;
          font-weight: 600 !important; letter-spacing: 0.22em !important;
          text-transform: uppercase !important; padding: 15px 28px !important;
          cursor: pointer !important; transition: background 0.2s !important; box-shadow: none !important;
        }
        .eoc-form-wrap input[type="submit"]:hover, .eoc-form-wrap button[type="submit"]:hover {
          background: #d4b87a !important;
        }
        .eoc-form-wrap label {
          font-family: Montserrat, sans-serif !important; font-size: 9px !important;
          font-weight: 600 !important; letter-spacing: 0.22em !important;
          text-transform: uppercase !important; color: #8a7040 !important;
          text-align: left !important; display: block !important; margin-bottom: 6px !important;
        }
      `}</style>

      <Toast message="You're in. Sharp thinking on its way to your inbox." visible={toastVisible} onHide={hideToast} light />
    </section>
  );
};


// ─── FINAL CTA ────────────────────────────────────────────────────────────────
export const FinalCTA: React.FC<BottomSectionsProps> = ({ onOpenModal }) => (
  <section
    id="final-cta"
    className="relative overflow-hidden text-center py-[140px] px-10"
    style={{ background: 'linear-gradient(200deg, #080807 0%, #0d0c0b 100%)' }}
  >
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      style={{ width: 700, height: 700, background: 'radial-gradient(circle, rgba(184,154,90,0.045) 0%, transparent 70%)' }}
    />
    <div className="relative max-w-[760px] mx-auto z-10">
      <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(38px, 5vw, 66px)', fontWeight: 300, color: '#f2ece0', lineHeight: 1.1, marginBottom: 20 }}>
        Your brand deserves
        <br />a system, not just
        <br /><em style={{ fontStyle: 'italic', color: '#b89a5a' }}>a social media plan.</em>
      </h2>
      <p style={{ fontSize: 14, fontWeight: 300, color: '#aea89f', maxWidth: 500, margin: '0 auto 52px', lineHeight: 2, letterSpacing: '0.02em' }}>
        If you're generating revenue but growth isn't compounding, the structure needs work. Let's find out exactly where — and build the architecture to fix it.
      </p>
      <div className="flex flex-wrap gap-5 justify-center">
        <button
          onClick={() => document.getElementById('clarity')?.scrollIntoView({ behavior: 'smooth' })}
          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: '#b89a5a', color: '#0d0c0b', padding: '16px 36px', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#d4b87a'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#b89a5a'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          Start With Clarity
        </button>
        <button
          onClick={() => onOpenModal('packages')}
          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: 'transparent', color: '#f2ece0', padding: '15px 36px', border: '1px solid rgba(242,236,224,0.2)', cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#b89a5a'; e.currentTarget.style.color = '#b89a5a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(242,236,224,0.2)'; e.currentTarget.style.color = '#f2ece0'; }}
        >
          View Packages
        </button>
      </div>
    </div>
  </section>
);


// ─── FOOTER ───────────────────────────────────────────────────────────────────
const footerNav = [
  { label: 'About', id: 'difference' },
  { label: 'Services', id: 'services' },
  { label: 'Our Method', id: 'method' },
  { label: 'Selected Work', id: 'work' },
  { label: 'Who We Serve', id: 'who' },
  { label: 'Get Clarity', id: 'clarity' },
];

const socials = [
  { label: 'Instagram', href: 'https://instagram.com/hnldigitalluxe' },
  { label: 'TikTok', href: 'https://tiktok.com/@hnldigitalluxe' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/hnldigitalluxe' },
];

export const Footer: React.FC<BottomSectionsProps> = ({ onOpenModal }) => (
  <footer style={{ background: 'linear-gradient(180deg, #181614 0%, #111009 100%)', borderTop: '1px solid rgba(184,154,90,0.1)', padding: '80px 40px 40px' }}>
    <div className="max-w-[1200px] mx-auto">

      {/* ── Top grid: brand / nav / contact ── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-[48px] mb-[64px]">

        {/* Brand — spans 5 cols */}
        <div className="md:col-span-5">
          <h4 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 24, fontWeight: 500, letterSpacing: '0.12em', color: '#f2ece0', marginBottom: 6 }}>
            HNL <span style={{ color: '#b89a5a' }}>Digital Luxe</span>
          </h4>
          <p style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 15, fontWeight: 300, fontStyle: 'italic', color: '#5a5450', marginBottom: 20, letterSpacing: '0.04em' }}>
            Structure behind the visibility.
          </p>
          <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2, maxWidth: 300, letterSpacing: '0.02em', marginBottom: 28 }}>
            Premium strategic digital marketing and operational growth for serious brands in hospitality, fashion, beauty, real estate, and lifestyle.
          </p>
          {/* Social links */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {socials.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#5a5450', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#b89a5a')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#5a5450')}
              >{s.label}</a>
            ))}
          </div>
        </div>

        {/* Nav links — spans 3 cols */}
        <div className="md:col-span-3">
          <h5 style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 24, fontFamily: 'Montserrat, sans-serif' }}>
            Navigate
          </h5>
          <ul className="list-none" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {footerNav.map((item) => (
              <li key={item.label}>
                <button
                  onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 300, color: '#78736c', letterSpacing: '0.04em', transition: 'color 0.2s', textAlign: 'left' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#f2ece0')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
                >{item.label}</button>
              </li>
            ))}
            <li>
              <button
                onClick={() => onOpenModal('packages')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 300, color: '#78736c', letterSpacing: '0.04em', transition: 'color 0.2s', textAlign: 'left' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#f2ece0')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
              >Packages</button>
            </li>
            <li>
              <a href="https://insights.hnldigitalluxe.com" target="_blank" rel="noreferrer"
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 300, color: '#78736c', letterSpacing: '0.04em', textDecoration: 'none', transition: 'color 0.2s', display: 'flex', alignItems: 'center', gap: 5 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#f2ece0')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
              >
                HNL Insights
                <svg width="8" height="8" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.4 }}>
                  <path d="M2 8L8 2M8 2H4M8 2V6" stroke="#b89a5a" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </li>
          </ul>
        </div>

        {/* Contact — spans 4 cols */}
        <div className="md:col-span-4">
          <h5 style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 24, fontFamily: 'Montserrat, sans-serif' }}>
            Get In Touch
          </h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
            <a href="mailto:contact@hnldigitalluxe.com"
              style={{ fontSize: 12, fontWeight: 300, color: '#78736c', textDecoration: 'none', letterSpacing: '0.04em', transition: 'color 0.2s', display: 'flex', alignItems: 'center', gap: 10 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#b89a5a')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
            >
              <span style={{ width: 16, height: 1, background: 'rgba(184,154,90,0.35)', display: 'inline-block', flexShrink: 0 }} />
              contact@hnldigitalluxe.com
            </a>
            <a href="tel:+2348062800289"
              style={{ fontSize: 12, fontWeight: 300, color: '#78736c', textDecoration: 'none', letterSpacing: '0.04em', transition: 'color 0.2s', display: 'flex', alignItems: 'center', gap: 10 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#b89a5a')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
            >
              <span style={{ width: 16, height: 1, background: 'rgba(184,154,90,0.35)', display: 'inline-block', flexShrink: 0 }} />
              0806 280 0289
            </a>
            <span style={{ fontSize: 12, fontWeight: 300, color: '#78736c', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 16, height: 1, background: 'rgba(184,154,90,0.35)', display: 'inline-block', flexShrink: 0 }} />
              Abuja, Nigeria
            </span>
          </div>

          {/* CTA buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => onOpenModal('workWithUs')}
              style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: '#b89a5a', color: '#0d0c0b', border: 'none', padding: '12px 20px', cursor: 'pointer', transition: 'background 0.2s', textAlign: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#d4b87a')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#b89a5a')}
            >Work With Us</button>
            <button
              onClick={() => onOpenModal('packages')}
              style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: 'transparent', color: '#b89a5a', border: '1px solid rgba(184,154,90,0.28)', padding: '11px 20px', cursor: 'pointer', transition: 'background 0.2s', textAlign: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(184,154,90,0.07)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >View Packages</button>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div
        className="flex flex-col md:flex-row justify-between items-center gap-4"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 28 }}
      >
        <p style={{ fontSize: 10, fontWeight: 300, color: '#3e3a36', letterSpacing: '0.08em' }}>
          © 2025 HNL Digital Luxe. Abuja, Nigeria. All rights reserved.
        </p>
        <p style={{ fontSize: 10, fontWeight: 300, color: '#3e3a36', letterSpacing: '0.08em' }}>
          @hnldigitalluxe
        </p>
      </div>

    </div>
  </footer>
);